import React, { ReactElement, useEffect, useState } from 'react';
import { LeaderIcon, mq, ErrorFilledIcon, theme } from '@kvdbil/components';
import styled from 'styled-components';

// Helpers
import { getDurationTo } from '~/helpers/time';
import PieClock from './PieClock';
import { useInterval } from '~/App/shared/hooks/useIntervalHook';
import { useTranslation } from '~/Locale';
import { timeDiff as timeDiffSelector } from '~/App/shared/selectors/global';
import { useDispatch, useSelector } from 'react-redux';
import { getAuction } from '~/App/shared/actions/auction';
import { useStableNow } from '~/App/shared/hooks/useStableNow';

const BiddingTimerHeader = styled.div<{ isLeading: boolean }>`
  font-size: 1.25em;
  color: ${({ theme, isLeading }) =>
    isLeading ? theme.colors.primary.mainAlt : theme.colors.text.dark};
  font-family: ${({ theme }) => theme.typography.fontAlternativeFamily};
  text-align: center;
  margin-bottom: 10px;

  svg {
    margin-right: 0.5rem;
    font-size: 1.2rem;
    color: ${({ isLeading, theme }) =>
      isLeading
        ? theme.colors.primary.mainAlt
        : theme.colors.grayscaleToned.dark3};
    margin-top: 0px;
  }

  ${mq(null, 'laptop')} {
    margin-bottom: 0;
  }
`;
const BiddingTimerInformation = styled.div`
  font-size: 1em;
  text-align: center;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.text.dark};
`;
const BiddingTimerClock = styled.div<{ isLeading: boolean }>`
  border: 1px solid
    ${({ isLeading, theme }) =>
      isLeading
        ? theme.colors.primary.mainAlt
        : theme.colors.grayscaleToned.light1};
  background: ${({ theme }) => theme.colors.background.light};
  border-radius: 50%;
  width: 114px;
  height: 114px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto;
  position: relative;
`;
const BiddingTimeStatus = styled.div<{ isLeading: boolean }>`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  color: ${({ isLeading, theme }) =>
    isLeading
      ? theme.colors.primary.mainAlt
      : theme.colors.grayscaleToned.light1};
  font-family: ${({ theme }) => theme.typography.fontAlternativeFamily};
  font-size: 1.125em;
`;

export interface BiddingTimerProps {
  closeAt: string;
  auctionId: string;
  isLeading: boolean;
  isParticipating: boolean;
  countdownDuration: number;
}
export const BiddingTimer = ({
  closeAt,
  auctionId,
  isLeading,
  isParticipating,
  countdownDuration
}: BiddingTimerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const timeDiff = useSelector(timeDiffSelector) ?? 0;
  const stableNow = useStableNow();

  const [duration, setDuration] = useState(() =>
    getDurationTo(timeDiff, new Date(closeAt), new Date(stableNow))
  );

  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const rawProgress = (1 - duration.as('seconds') / countdownDuration) * 100;
  const progress = Math.min(Math.max(0, rawProgress), 100);

  const isClosing = minutes <= 0 && seconds <= 0;

  const runCountdown = () => {
    const duration = getDurationTo(timeDiff, new Date(closeAt));
    setDuration(duration);
  };

  useInterval(() => {
    runCountdown();
  }, 1000);

  useEffect(() => {
    if (isClosing && auctionId) {
      dispatch(getAuction(auctionId));
    }
  }, [isClosing, auctionId, dispatch]);

  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = seconds.toString().padStart(2, '0');
  const status = isClosing ? t('Closing') : `${paddedMinutes}:${paddedSeconds}`;

  let header = t('Countdown');
  let icon: ReactElement | null = null;
  let information = t('The auction will end soon.');

  if (isParticipating && isLeading) {
    header = t('Countdown - you are leader');
    icon = <LeaderIcon />;
  }

  if (isParticipating && !isLeading) {
    header = t('Countdown - you are outbid');
    icon = <ErrorFilledIcon />;
    information = t("Be sure to place a bid before it's too late");
  }

  return (
    <div>
      <BiddingTimerClock isLeading={isLeading}>
        <PieClock
          progress={progress}
          borderColorBackground={
            isLeading
              ? theme.colors.primary.mainAlt
              : theme.colors.grayscaleToned.light1
          }
          borderColorForeground={theme.colors.grayscaleToned.light4}
        />
        <BiddingTimeStatus isLeading={isLeading}>{status}</BiddingTimeStatus>
      </BiddingTimerClock>
      <BiddingTimerHeader isLeading={isLeading}>
        {icon}
        {header}
      </BiddingTimerHeader>
      <BiddingTimerInformation>{information}</BiddingTimerInformation>
    </div>
  );
};

export default BiddingTimer;
